import { configs } from '$configs';
import { SORT, COLORS, DATE_FORMATS } from '$gbusiness/enums';
import CellModel from '$gbusiness/models/cell';
import { format } from '$gbusiness/helpers/date';
import { defaultDataSource, defaultStyles, defaultTable, TableModel } from '$gbusiness/models/table';

import { currency } from '$gbusiness/helpers/util';
import { Button } from '$gcomponents/primitives';
import intl from '$intl';
import { deriveRawToInvoice } from '$fbusiness/models/invoice';
import { CLOSE_OPTIONS, INVOICE_STATUS } from '$fbusiness/enums/options/invoiceStatus';
import { ORDER_STATUS } from '$fbusiness/enums/options/orderStatus';
import { Select } from '@mui/material';
import { generateSelectOptions } from '$gcomponents/utils/input';

export const INVOICES_TABLE_CONFIG: TableModel = {
  dataSource: {
    ...defaultDataSource,
    endpoint: configs.api.invoice.general,
    // mockData,
    deriveToModel: deriveRawToInvoice,
    method: 'GET',
    defaultSortKey: 'updatedAt',
    defaultSortOrder: SORT.DSC,
    pageSize: 20,
    autoRefreshInterval: 30,
    innerKey: 'payments',
  },
  styles: {
    ...defaultStyles,
    exclusion: 'invoices',
    color: COLORS.LIGHT,
    fontSize: '1.05em',
    minWidth: 800,
    maxWidth: 1400,
    cellPadding: ` 8px 4px`,
    responsive: true,
    isStripped: false,
    rowHeight: 44,
    headerHeight: 64,
    setRowClass: (row) => (row.overdue ? 'overdue' : ''),
  },
  innerConfig: {
    ...defaultTable,
    styles: {
      ...defaultStyles,
      minWidth: 800,
      maxWidth: 1000,
      fontSize: '0.9em',
      cellPadding: '2px 2px',
      isStripped: false,
      rowHeight: 30,
      headerHeight: 30,
    },
  },
};

export const defaultInvoiceColumns = ['createdAt', 'status', 'total', 'balance'];

export const INVOICE_TABLE_CONFIG: TableModel = {
  dataSource: {
    ...defaultDataSource,
    endpoint: configs.api.invoice.general,
    // mockData,
    deriveToModel: deriveRawToInvoice,
    method: 'GET',
    defaultSortKey: 'updatedAt',
    defaultSortOrder: SORT.DSC,
    pageSize: 20,
    autoRefreshInterval: 30,
    innerKey: 'payments',
  },
  styles: {
    ...defaultStyles,
    color: COLORS.LIGHT,
    fontSize: '1.05em',
    minWidth: 800,
    maxWidth: 1200,
    cellPadding: ` 8px 4px`,
    responsive: true,
    isStripped: false,
    rowHeight: 44,
    headerHeight: 56,
    setRowClass: (row) => (row.overdue ? 'overdue' : ''),
  },
  innerConfig: {
    ...defaultTable,
    styles: {
      ...defaultStyles,
      minWidth: 800,
      maxWidth: 1000,
      fontSize: '0.9em',
      cellPadding: '2px 2px',
      isStripped: false,
      rowHeight: 30,
      headerHeight: 30,
    },
  },
};

export const INVOICES_TABLE = ({
  onCloseOrder,
  onViewInvoice,
  onViewShipping,
  onChangeStatus,
  isReceiving,
  isPickupOnly,
  hidePrice,
  isSalesman,
}): Array<CellModel> => [
  {
    label: 'SCREEN.INVOICES.COLS.CREATED',
    value: 'createdAt',
    component: (row) => <>{format(row?.createdAt, DATE_FORMATS.SLASH) || ''}</>,
    sortable: SORT.ASC,
    width: 200,
  },
  {
    isHidden: !isSalesman,
    label: 'SCREEN.INVOICES.COLS.STORE',
    value: 'store.name',
    alwaysShow: true,
    sortKey: 'stores.name',
    sortable: SORT.ASC,
    width: 280,
  },
  {
    label: 'SCREEN.INVOICES.COLS.' + (isReceiving ? 'RECEIVING_NO' : 'INVOICE_NO'),
    value: 'invoiceNumber',
    alwaysShow: true,
    className: 'main',
    width: 250,
  },
  {
    label: 'SCREEN.INVOICES.COLS.ORDER_ID',
    value: 'order.orderId',
    className: 'desktop',
    align: 'center',
    width: 120,
  },
  {
    label: 'SCREEN.INVOICES.COLS.' + (isPickupOnly ? 'PICKUP_DATE' : 'DUE_DATE'),
    value: 'dueDate',
    component: (row) => <>{format(row.dueDate || '', DATE_FORMATS.DATE_WEEKDAY) || ''}</>,
    sortable: SORT.ASC,
    sortKey: 'dueDate',
    width: 200,
  },
  {
    label: 'SCREEN.INVOICES.COLS.' + (isPickupOnly ? 'PICKUP_DATE' : 'DELIVERY_DATE'),
    value: 'deliveryDate',
    component: (row) => <>{row.order ? format(row.order.deliveryDate, DATE_FORMATS.SLASH) || '' : ''}</>,
    sortable: SORT.ASC,
    width: 180,
  },
  // {
  //   label: 'SCREEN.INVOICES.COLS.DELIVERED_DATE',
  //   value: 'deliveredAt',
  //   component: row => <>{format(row.order.confirmedAt, DATE_FORMATS.SLASH) || ''}</>,
  //   sortable: SORT.ASC,
  //   width: 200,
  // },
  // {
  //   label: 'SCREEN.INVOICES.COLS.STATUS',
  //   value: 'order.status',
  //   align: 'center',
  //   width: 150,
  // },
  {
    label: 'SCREEN.INVOICES.COLS.QTY',
    value: 'order.qty',
    component: (row) => <>{row.order?.qty || 0}</>,
    className: 'desktop',
    align: 'center',
    width: 120,
  },
  {
    label: 'SCREEN.INVOICES.COLS.QTY_SENT',
    value: 'qtySent',
    className: 'desktop',
    align: 'center',
    width: 120,
  },
  {
    label: 'SCREEN.INVOICES.COLS.QTY_RECEIVED',
    value: 'qtyReceived',
    component: (row) => <>{row.qtyReceived || ''}</>,
    sortable: SORT.ASC,
    width: 150,
  },
  {
    isHidden: isPickupOnly,
    label: 'SCREEN.INVOICES.COLS.SHIPPED_TO',
    value: 'order.city',
    width: 200,
  },
  {
    isHidden: hidePrice,
    label: 'SCREEN.INVOICES.COLS.TOTAL',
    value: 'total',
    component: (row) => <>{currency(row.total)}</>,
    sortable: SORT.ASC,
    width: 170,
  },
  {
    label: 'SCREEN.INVOICES.COLS.BALANCE',
    value: 'balance',
    component: (row) => (
      <div className={`outlined ${row.balance === 0 && 'success bold'}`}>{currency(row.balance || 0)}</div>
    ),
    sortable: SORT.ASC,
    sortKey: 'balance',
    width: 150,
  },
  {
    label: 'SCREEN.INVOICES.COLS.TERM',
    value: 'paymentTerm',
    component: (row) => <>{row.term?.name || ''}</>,
    width: 200,
  },
  {
    isHidden: isReceiving,
    label: 'SCREEN.INVOICES.COLS.INVOICE_STATUS',
    value: 'status',
    className: 'no-label',
    component: (row) => {
      if (row.status === INVOICE_STATUS.PAID) return <div>{intl('INPUT.OPTION.INVOICE_STATUS.PAID')}</div>;
      const options = CLOSE_OPTIONS;
      return row.closedAt && false ? (
        <Select
          variant="outlined"
          className="xsmall"
          value={row.status}
          onChange={(e) => onChangeStatus(row, e.target.value)}>
          {generateSelectOptions(options, row.status, true)}
        </Select>
      ) : (
        <div>{intl('INPUT.OPTION.INVOICE_STATUS.' + row.status)}</div>
      );
    },
    sortable: SORT.ASC,
    width: 141,
  },
  {
    label: '',
    value: '',
    align: 'center',
    className: 'action',
    alwaysShow: true,
    component: (row) => (
      <>
        <Button size="small" variant="text" onClick={() => onViewInvoice(row)}>
          {intl('SCREEN.INVOICES.ACTIONS.VIEW')}
        </Button>
        {(row.order?.status === ORDER_STATUS.DELIVERED || row.order?.status === ORDER_STATUS.COMPLETED) &&
          !row.flaggedAt && (
            <div>
              <Button size="small" variant="text" onClick={() => onViewInvoice(row)}>
                {intl('SCREEN.INVOICES.ACTIONS.VIEW')}
              </Button>
            </div>
          )}
      </>
    ),
    width: 200,
  },
  // {
  //   label: '',
  //   value: '',
  //   align: 'center',
  //   alwaysShow: true,
  //   component: (row) =>
  //     row.confirmedAt || true ? (
  //       <Button size="small" variant="text" onClick={() => onViewShipping(row)}>
  //         {intl('SCREEN.INVOICES.ACTIONS.SHIP_DETAILS')}
  //       </Button>
  //     ) : null,
  //   width: 220,
  // },
];
